/**
 * BackgroundMusic - Native HTML5 audio background music with floating mini player
 *
 * Persistent singleton. Starts after preloader completes.
 * Shows a floating play/pause button and mini audio wave in the top-right.
 * MP3 path: /playsometunes.mp3 (web root).
 */

import { Preloader } from "./preloader";

const MUSIC_URL = "/playsometunes.mp3";
const GESTURE_EVENTS = ["click", "touchstart", "keydown"];
const DEBOUNCE_DELAY = 300; // Prevent rapid clicks
const DEBUG = false; // Set to true for debugging

// Debug logger - only logs if DEBUG is true
const log = (...args) => DEBUG && console.log(...args);
const warn = (...args) => DEBUG && console.warn(...args);
const error = (...args) => console.error(...args); // Always log errors

class _BackgroundMusic {
  #audio = null;
  #container = null;
  #playPauseBtn = null;
  #waveBars = null;
  #initialized = false;
  #unlockHandler = null;
  #isToggling = false; // Debounce flag
  #playAttempts = 0; // Track play attempts to prevent infinite loops

  init() {
    if (this.#initialized) return;

    this.#initialized = true;

    log('[BackgroundMusic] Initializing...');

    // Create native HTML5 audio element
    this.#audio = new Audio(MUSIC_URL);
    this.#audio.loop = true;
    this.#audio.volume = 0.4;
    this.#audio.preload = "auto";

    // Prevent multiple instances by ensuring only one audio element exists
    this.#audio.setAttribute('data-background-music', 'true');

    // Event listeners for state management
    this.#audio.addEventListener('play', () => {
      log('[BackgroundMusic] Audio playing');
      this.#playAttempts = 0;
      this.#setPlaying(true);
    });

    this.#audio.addEventListener('pause', () => {
      log('[BackgroundMusic] Audio paused');
      this.#setPlaying(false);
    });

    this.#audio.addEventListener('error', (e) => {
      error('[BackgroundMusic] Failed to load audio:', e);
    });

    this.#audio.addEventListener('loadeddata', () => {
      log('[BackgroundMusic] Audio loaded successfully');
    });

    this.#audio.addEventListener('canplaythrough', () => {
      log('[BackgroundMusic] Audio can play through');
    });

    Preloader.onComplete(() => {
      log('[BackgroundMusic] Preloader complete, creating player UI');
      this.#createPlayer();
      // Try to play after a short delay
      setTimeout(() => this.#attemptPlay(), 100);
    });
  }

  #createPlayer() {
    this.#container = document.createElement("div");
    this.#container.className = "background-music-player";
    this.#container.setAttribute("aria-label", "Background music");

    this.#playPauseBtn = document.createElement("button");
    this.#playPauseBtn.type = "button";
    this.#playPauseBtn.className = "background-music-player__btn";
    this.#playPauseBtn.setAttribute("aria-label", "Play background music");
    this.#playPauseBtn.innerHTML = this.#svgPlay();
    this.#playPauseBtn.addEventListener("click", (e) => {
      log('[BackgroundMusic] Button clicked');
      e.preventDefault();
      e.stopPropagation();
      this.#toggle();
    });

    const wave = document.createElement("div");
    wave.className = "background-music-player__wave";
    wave.setAttribute("aria-hidden", "true");
    this.#waveBars = [];
    for (let i = 0; i < 5; i++) {
      const bar = document.createElement("span");
      bar.className = "background-music-player__bar";
      wave.appendChild(bar);
      this.#waveBars.push(bar);
    }

    this.#container.appendChild(this.#playPauseBtn);
    this.#container.appendChild(wave);
    document.body.appendChild(this.#container);

    // Trigger fade-in and slide-up animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#container.classList.add("is-visible");
      });
    });

    // Set initial state based on audio element
    this.#setPlaying(!this.#audio.paused);
  }

  #svgPlay() {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>`;
  }

  #svgPause() {
    return `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>`;
  }

  #setPlaying(playing) {
    if (!this.#container) return;
    this.#container.classList.toggle("is-playing", playing);
    this.#playPauseBtn.setAttribute("aria-label", playing ? "Pause background music" : "Play background music");
    this.#playPauseBtn.innerHTML = playing ? this.#svgPause() : this.#svgPlay();
  }

  async #toggle() {
    // Debounce to prevent rapid clicks causing multiple instances
    if (this.#isToggling) return;
    this.#isToggling = true;

    try {
      if (!this.#audio.paused) {
        // Pause - this will actually pause, not reset
        log('[BackgroundMusic] Pausing at position:', this.#audio.currentTime);
        this.#audio.pause();
      } else {
        // Play - resume from current position
        log('[BackgroundMusic] Resuming from position:', this.#audio.currentTime);
        await this.#play();
      }
    } catch (e) {
      warn('[BackgroundMusic] Toggle failed:', e);
    } finally {
      // Reset debounce flag after delay
      setTimeout(() => {
        this.#isToggling = false;
      }, DEBOUNCE_DELAY);
    }
  }

  async #play() {
    if (!this.#audio) {
      warn('[BackgroundMusic] No audio element');
      return;
    }

    try {
      // Check if audio is already playing to prevent multiple instances
      if (!this.#audio.paused) {
        log('[BackgroundMusic] Already playing, skipping');
        return;
      }

      log('[BackgroundMusic] Attempting to play...');
      const playPromise = this.#audio.play();

      if (playPromise !== undefined) {
        await playPromise;
        log('[BackgroundMusic] Play successful');
      }
    } catch (e) {
      log('[BackgroundMusic] Play failed:', e.name, e.message);

      // Autoplay was prevented - setup unlock on user gesture
      if (e.name === 'NotAllowedError') {
        log('[BackgroundMusic] Autoplay blocked - waiting for user gesture');
        this.#setupUnlockOnGesture();
      } else {
        warn('[BackgroundMusic] Play error:', e);
      }
    }
  }

  #attemptPlay() {
    if (!this.#audio) {
      warn('[BackgroundMusic] No audio element in attemptPlay');
      return;
    }

    if (this.#playAttempts > 2) {
      log('[BackgroundMusic] Max play attempts reached');
      return;
    }

    this.#playAttempts++;
    log(`[BackgroundMusic] Attempt #${this.#playAttempts} to play`);

    this.#play();
  }

  /**
   * Autoplay requires a user gesture. Add one-time listeners
   * so the first click/touch/keypress anywhere on the page unlocks and plays.
   */
  #setupUnlockOnGesture() {
    if (this.#unlockHandler) {
      log('[BackgroundMusic] Unlock handler already set');
      return;
    }

    log('[BackgroundMusic] Setting up unlock on gesture');

    this.#unlockHandler = (e) => {
      log('[BackgroundMusic] User gesture detected, attempting unlock');

      // Remove listeners immediately to prevent re-triggering
      GESTURE_EVENTS.forEach((ev) => document.removeEventListener(ev, this.#unlockHandler, true));
      this.#unlockHandler = null;

      // Reset play attempts and try to play
      this.#playAttempts = 0;

      // Don't interfere if they clicked the music button specifically
      // Check if the click was on our button or its children
      const musicButton = this.#playPauseBtn;
      if (musicButton && e.target && (e.target === musicButton || musicButton.contains(e.target))) {
        log('[BackgroundMusic] Unlock via music button - letting toggle handle it');
        return;
      }

      this.#play();
    };

    // Use capture phase to ensure we catch the event
    GESTURE_EVENTS.forEach((ev) => document.addEventListener(ev, this.#unlockHandler, true));
  }

  /**
   * Debug method - logs current audio state
   * Call from console: BackgroundMusic.debug()
   */
  debug() {
    if (!this.#audio) {
      console.log('[BackgroundMusic] No audio element initialized');
      return;
    }

    console.log('[BackgroundMusic] Debug Info:', {
      src: this.#audio.src,
      paused: this.#audio.paused,
      currentTime: this.#audio.currentTime,
      duration: this.#audio.duration,
      volume: this.#audio.volume,
      muted: this.#audio.muted,
      readyState: this.#audio.readyState,
      networkState: this.#audio.networkState,
      error: this.#audio.error,
      playAttempts: this.#playAttempts,
      isToggling: this.#isToggling,
      hasUnlockHandler: !!this.#unlockHandler
    });

    // Try to play manually
    console.log('[BackgroundMusic] Attempting manual play...');
    this.#play();
  }

  destroy() {
    log('[BackgroundMusic] Destroying...');

    // Remove unlock handler if present
    if (this.#unlockHandler) {
      GESTURE_EVENTS.forEach((ev) => document.removeEventListener(ev, this.#unlockHandler, true));
      this.#unlockHandler = null;
    }

    // Stop and cleanup audio
    if (this.#audio) {
      this.#audio.pause();
      this.#audio.src = '';
      this.#audio.load(); // Reset audio element
      this.#audio = null;
    }

    // Remove UI elements
    if (this.#container?.parentNode) {
      this.#container.parentNode.removeChild(this.#container);
    }

    this.#container = null;
    this.#playPauseBtn = null;
    this.#waveBars = null;
    this.#initialized = false;
    this.#isToggling = false;
    this.#playAttempts = 0;
  }
}

export const BackgroundMusic = new _BackgroundMusic();
export { _BackgroundMusic };
